import { safeboxClient } from 'myNet'
import { updateToken } from 'services/jwt'
import { unregister as unregisterTotpFingerprint } from 'services/totp/duplicateRegistry'
// 검증 로직은 leaf 모듈에 둔다 — `src/api/` 를 커버하는 CI 잡이 꺼져 있어(frontend-ci.yml:104)
// 여기 붙인 테스트는 CI 에서 실행되지 않는다. 상세 사유는 multipartGuard.js 머리말 참조.
import { requireResourceId } from 'services/attachmentV2/multipartGuard'

const ROUTE = '/v1/resources'

const ResourceService = {
  create: async (request) => {
    const response = await safeboxClient.xhrPost(`${ROUTE}`, request, null, null, {})
    const { code, message } = response.data
    if (code !== 200) {
      if (code === 401) {
        updateToken()
          .then(() => {})
          .catch(() => {
            const err = new Error(message)
            err.code = code
            throw err
          })
      } else {
        const err = new Error(message)
        err.code = code
        err.data = response.data.data
        throw err
      }
    }
    return response.data
  },
  getAll: async (request) => {
    const response = await safeboxClient.get(`${ROUTE}`, request)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },
  get: async ({ id, isZDW = false, ...params }) => {
    const requestParams = { ...params }
    if (isZDW === true) {
      requestParams.view = 'zdw'
    }
    const response = await safeboxClient.get(`${ROUTE}/${id}`, requestParams)
    return {
      ...response.data,
      headerAccessLevel: response.origin.headers.get('x-file-access-level'),
    }
  },
  put: async ({ id, request, sendNotification = false }) => {
    const response = await safeboxClient.xhrPut(
      `${ROUTE}/${id}?send_notification=${encodeURIComponent(sendNotification)}`,
      request,
      null,
      null,
    )
    const { code, message } = response.data
    if (code !== 200) {
      if (code === 401) {
        updateToken()
          .then(() => {})
          .catch(() => {
            const err = new Error(message)
            err.code = code
            throw err
          })
      } else {
        const err = new Error(message)
        err.code = code
        throw err
      }
    }
    return response.data
  },
  delete: async ({ id }) => {
    const response = await safeboxClient.delete(`${ROUTE}/${id}`)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    // TOTP 중복 감지 지문 제거(#1506) — 삭제된 레코드의 지문이 세션에 남아 유령
    // 배지/경고를 만드는 것을 방지. 호출처(RecordEdit/RecordDetail) 공통 chokepoint.
    unregisterTotpFingerprint(id)
    return response.data
  },
  info: async () => {
    const response = await safeboxClient.get(`${ROUTE}/info`)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },
  prepareShare: async (id, params) => {
    const response = await safeboxClient.post(`${ROUTE}/${id}/prepare-share`, params)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },
  share: async (id, params, sendNotification = false) => {
    const response = await safeboxClient.post(
      `${ROUTE}/${id}/share?send_notification=${encodeURIComponent(sendNotification)}`,
      params,
    )
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },
  deleteShare: async (id, params) => {
    const response = await safeboxClient.delete(`${ROUTE}/${id}/share`, params)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },
  download: (etag) => {
    return safeboxClient.xhrGet(`/v1/file/${etag}`)
  },
  putSharePermission: async (id, params) => {
    const response = await safeboxClient.put(`${ROUTE}/${id}/share/permission`, params)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },
  bulkPutLeaked: async (data) => {
    const response = await safeboxClient.post(`${ROUTE}/leaked/bulk`, data)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }

    return response.data
  },
  getLeaked: async () => {
    const response = await safeboxClient.get(`${ROUTE}`, { leaked: true })
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }

    return response.data
  },

  prepareTransferOwner: async (resourceId, params) => {
    const response = await safeboxClient.post(
      `${ROUTE}/${resourceId}/prepare-transfer-owner`,
      params,
    )
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },

  transferOwner: async (resourceId, params) => {
    const response = await safeboxClient.patch(`/v1/resources/${resourceId}/owner`, params)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },

  // `vaultMigration` — 금고 포맷 마이그레이션 스윕 전용. 이 엔드포인트의 Activity 는
  // "레코드 내보내기" 로 기록되는데(activity_constants.py 의 `bulk_get_resource` 매핑),
  // 스윕은 사용자 행위가 아니라 시스템 스캔이라 감사 목록을 오염시킨다.
  // 🛑 **키를 조건부로만 넣는다** — `query-string` 8.2.0 은 `false` 를 생략하지 않고
  //    `vault_migration=false` 로 전송한다(실측). 진짜 내보내기(`utils/importExport/bulkExport.js`)는
  //    이 옵션을 넘기지 않으므로 파라미터 자체가 없어야 한다.
  bulkGet: async (scope, offset = 0, limit = 100, { vaultMigration = false } = {}) => {
    const response = await safeboxClient.get(`${ROUTE}/bulk`, {
      scope,
      offset,
      limit,
      ...(vaultMigration ? { vault_migration: true } : {}),
    })
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return {
      ...response.data,
      fileAccessLevel: response.origin?.headers?.get('x-file-access-level') ?? null,
    }
  },

  // 금고 정방향 스윕(PR 7-a)이 유일한 호출자다. 암호값 컬럼 하나를 **이전 값과 비교해**
  // 교체한다(CAS) — 일반 저장 API 를 쓰지 않는 이유는 그쪽이 item 배열 전량 재전송을
  // 요구하고, 그것을 한 건이라도 틀리면 항목이 영구 삭제되기 때문이다
  // (`backend/routers/v1/resource.py:1173-1181`).
  rewriteVaultFormat: async (targets) => {
    const response = await safeboxClient.post(`${ROUTE}/vault-format/rewrite`, { targets })
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data.data
  },

  getDownloadUrls: async (files, purpose = 'download', skipActivity = false) => {
    const query = new URLSearchParams({ purpose })
    if (skipActivity) query.set('skip_activity', 'true')
    const response = await safeboxClient.post(`${ROUTE}/files/download-urls?${query}`, {
      files,
    })
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },

  getUploadUrls: async (files, resourceId) => {
    const url = resourceId ? `${ROUTE}/${resourceId}/upload-urls` : `${ROUTE}/upload-urls`
    const response = await safeboxClient.post(url, { files })
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },

  // ── 대용량 첨부 멀티파트 (PR #1779 백엔드와 1:1) ──────────────────────
  // ⚠️ initiate 응답의 part_urls 는 **서명 자격증명**이다 — 로그·에러 리포트에 통째로 남기지 않는다.

  initiateMultipart: async (resourceId, body) => {
    requireResourceId(resourceId)
    const response = await safeboxClient.post(`${ROUTE}/${resourceId}/files/multipart`, body)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },

  issueMultipartPartUrls: async (resourceId, params) => {
    requireResourceId(resourceId)
    // 쿼리 직렬화는 클라이언트가 한다 (restClient/index.js:232-240 queryString.stringify)
    const response = await safeboxClient.get(
      `${ROUTE}/${resourceId}/files/multipart/part-urls`,
      params,
    )
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },

  completeMultipart: async (resourceId, body) => {
    requireResourceId(resourceId)
    const response = await safeboxClient.post(
      `${ROUTE}/${resourceId}/files/multipart/complete`,
      body,
    )
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },

  abortMultipart: async (resourceId, body) => {
    requireResourceId(resourceId)
    const response = await safeboxClient.post(`${ROUTE}/${resourceId}/files/multipart/abort`, body)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },

  createPresigned: async (request, params) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    const response = await safeboxClient.post(`${ROUTE}/presigned${query}`, request)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      err.data = response.data.data
      throw err
    }
    return response.data
  },

  // skipActivity: 메모 자동저장의 2회차 이후 저장에서 활동 로그 생략 (서버는 메모 리소스에만 적용)
  putPresigned: async ({ id, data, sendNotification = false, skipActivity = false }) => {
    const query = new URLSearchParams({ send_notification: String(sendNotification) })
    if (skipActivity) query.set('skip_activity', 'true')
    const response = await safeboxClient.put(`${ROUTE}/${id}/presigned?${query.toString()}`, data)
    const { code, message } = response.data
    if (code !== 200) {
      if (code === 401) {
        updateToken()
          .then(() => {})
          .catch(() => {
            const err = new Error(message)
            err.code = code
            throw err
          })
      } else {
        const err = new Error(message)
        err.code = code
        throw err
      }
    }
    return response.data
  },

  shareExtensionRequest: async (resourceId, params) => {
    const response = await safeboxClient.post(
      `/v1/resources/${resourceId}/share/extension-request`,
      params,
    )
    const { code, message } = response.data

    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      err.data = response.data.data
      throw err
    }
    return response.data
  },

  bulkDelete: async (resourceIds) => {
    const response = await safeboxClient.post(`/__test/resources/delete`, {
      resource_ids: resourceIds,
    })
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },
}

export default ResourceService
